import { Button } from '@repo/ui/components/ui/button'
import { CardContent } from '@repo/ui/components/ui/card'
import { surfaceSpacing } from '@repo/ui/lib/design-tokens'
import { cn } from '@repo/ui/lib/utils'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, BriefcaseBusiness, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'
import { PremiumPanel } from '@/components/ui/premium-panel'
import type { OperatorConfigurationState } from '@/lib/operator-config/types'

export function OperatorWorkspaceRouteGate({
  configuration,
  isLoading,
  error,
  onRetry,
  loadingCopy,
  resourceLabel,
  noOperatorTitle,
  noOperatorDescription,
  children,
}: {
  configuration: OperatorConfigurationState
  isLoading: boolean
  error: { message: string } | null
  onRetry?: () => Promise<void> | void
  loadingCopy: string
  resourceLabel: string
  noOperatorTitle: string
  noOperatorDescription: string
  children: ReactNode
}) {
  if (isLoading) {
    return (
      <PremiumPanel className="h-full min-h-full py-0">
        <CardContent
          className={cn(
            surfaceSpacing.insetY.roomy,
            'flex h-full min-h-full flex-col items-center justify-center py-12 text-center text-sm text-muted-foreground'
          )}
        >
          <div className="space-y-2">
            <p className="text-[11px] font-medium tracking-[0.18em] text-muted-foreground uppercase">
              Operator workspace
            </p>
            <p className="text-base font-semibold text-foreground">{loadingCopy}</p>
          </div>
        </CardContent>
      </PremiumPanel>
    )
  }

  if (error) {
    return (
      <PremiumPanel tone="destructive" className="h-full min-h-full py-0">
        <CardContent
          className={cn(
            surfaceSpacing.insetY.roomy,
            'flex h-full min-h-full flex-col items-center justify-center space-y-3 py-12 text-center'
          )}
        >
          <div className="space-y-3">
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full border border-destructive/40 bg-destructive/10">
              <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold">Couldn&apos;t load {resourceLabel}.</p>
              <p className="text-sm text-destructive/90">{error.message}</p>
            </div>
          </div>
          {onRetry ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="cursor-pointer gap-2"
              onClick={() => void onRetry()}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Retry
            </Button>
          ) : null}
        </CardContent>
      </PremiumPanel>
    )
  }

  if (!configuration.operator) {
    return (
      <PremiumPanel className="h-full min-h-full py-0">
        <CardContent
          className={cn(
            surfaceSpacing.insetY.roomy,
            'flex h-full min-h-full flex-col items-center justify-center space-y-3 py-12 text-center'
          )}
        >
          <div className="space-y-3">
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
              <BriefcaseBusiness className="h-4 w-4 shrink-0 text-primary" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-semibold text-foreground">{noOperatorTitle}</p>
              <p className="text-sm text-muted-foreground">{noOperatorDescription}</p>
            </div>
          </div>
          <Button asChild size="sm" className="cursor-pointer gap-2">
            <Link to="/setup">Open setup</Link>
          </Button>
        </CardContent>
      </PremiumPanel>
    )
  }

  return children
}
